import React, { useState, useEffect, useContext } from "react"
import { LogIn, LogOut } from "lucide-react"

import { BookingContext } from "../../../utils/providers/isolated-booking"
import { db } from "../../../utils/firebase"

import styles from "./practical.module.scss"

/**
 * Check-in & check-out times — one card per hotel on the booking, showing the
 * resort's check-in / check-out times alongside a photo.
 */
export default function CheckInTimes() {
	const { bookingID, hotelsOrder } = useContext(BookingContext)
	const [hotels, setHotels] = useState([])

	useEffect(() => {
		if (!bookingID || !hotelsOrder?.length) return
		let cancelled = false

		const load = async () => {
			const results = await Promise.all(
				hotelsOrder.map(async (hid) => {
					const resortID = (await db.doc(`bookings/${bookingID}/hotels/${hid}`).get()).data()?.resortID
					if (!resortID) return null

					const resort = (await db.doc(`hotels/${resortID}`).get()).data() || {}
					const imgs = await db.collection(`hotels/${resortID}/images`).limit(1).get()

					return {
						id: `${hid}-${resortID}`,
						name: resort.name || "",
						checkIn: resort.check_in || "",
						checkOut: resort.check_out || "",
						image: imgs.empty ? "" : imgs.docs[0].data().public_url,
					}
				}),
			)

			if (!cancelled) setHotels(results.filter(Boolean))
		}

		load().catch((err) => console.log("Check-in times load failed:", err))
		return () => {
			cancelled = true
		}
	}, [bookingID, hotelsOrder])

	return (
		<div className={styles.page}>
			<header className={styles.head}>
				<h1>Check-in &amp; check-out</h1>
				<p>Arrival and departure times for your hotels. Please confirm with the hotel before you travel.</p>
			</header>

			{hotels.length === 0 ? (
				<div className={styles.empty}>Check-in times aren't available yet. Please contact your agent for details.</div>
			) : (
				<div className={styles.cards}>
					{hotels.map((hotel) => (
						<div
							key={hotel.id}
							className={styles.card}>
							{hotel.image ? (
								<div
									className={styles.cardImage}
									style={{ backgroundImage: `url("${hotel.image}")` }}
								/>
							) : null}
							<div className={styles.cardBody}>
								<h3>{hotel.name || "Your hotel"}</h3>
								<div className={styles.times}>
									<div className={styles.time}>
										<span>
											<LogIn size={13} />
											Check in
										</span>
										<strong>{hotel.checkIn || "TBC"}</strong>
									</div>
									<div className={styles.time}>
										<span>
											<LogOut size={13} />
											Check out
										</span>
										<strong>{hotel.checkOut || "TBC"}</strong>
									</div>
								</div>
							</div>
						</div>
					))}
				</div>
			)}

			<p className={styles.timesNote}>Times can vary by hotel and season, so always double-check directly with the property before arrival.</p>
		</div>
	)
}
